import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import api from '../lib/api'
import { TOKEN_STORAGE_KEY, IMPERSONATE_STORAGE_KEY } from '../lib/constants'

interface User {
  id: number
  email: string
  name: string
  role: string
  focus_area_code?: string | null
  focus_area_role?: string | null
  preferred_focus_areas?: string[]
  bio?: string | null
  visible_in_directory?: boolean
  talent_profile_active?: boolean
  /** LT viewer (ticket #18): cross-FA read; routes to the review landing. */
  views_all_demands?: boolean
  /** Set once a no-FA council answered "I do not lead one focus area" (ticket #27). */
  onboarding_completed_at?: string | null
  /** The DSAI cluster this person leads, if any. */
  dsai_cluster_lead?: string | null
  // Self-declared links
  github_handle?: string | null
  // MS Graph enrichment (added by c880624; AuthContext was missed in
  // that commit so useEffectiveUser hits TS errors. Mirror the fields
  // from frontend/src/types/index.ts).
  job_title?: string | null
  department?: string | null
  office_location?: string | null
  company?: string | null
  country?: string | null
}

interface AuthContextType {
  user: User | null
  token: string | null
  loading: boolean
  login: (token: string) => void
  logout: () => void
  /**
   * Adopt the user a profile save returned (PUT /auth/me).
   *
   * The user object is plain `useState` refreshed only when the TOKEN changes,
   * so nothing a profile edit does can move it. `useProfile` first invalidated
   * a React Query key no query was registered under, then re-read /auth/me
   * after each save. Both left the page on the pre-edit value for a while, and
   * the FA multi-select wrote that stale array back over the newer one.
   */
  applyUser: (next: User) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => {
    const existing = localStorage.getItem(TOKEN_STORAGE_KEY)
    if (existing) return existing
    // One-time migration: copy token from old UI key if present, then remove legacy key
    const legacy = localStorage.getItem('token')
    if (legacy) {
      localStorage.setItem(TOKEN_STORAGE_KEY, legacy)
      localStorage.removeItem('token')
      return legacy
    }
    return null
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) {
      setLoading(false)
      return
    }

    api.get('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        localStorage.removeItem('token')
        setToken(null)
      })
      .finally(() => setLoading(false))
  }, [token])

  const login = (newToken: string) => {
    localStorage.removeItem(IMPERSONATE_STORAGE_KEY)
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken)
    setToken(newToken)
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(IMPERSONATE_STORAGE_KEY)
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, applyUser: setUser }}>
      {children}
    </AuthContext.Provider>
  )
}
